import React, {useCallback, useLayoutEffect, useMemo} from 'react';
import {View} from 'react-native';
import {useSelector} from 'react-redux';
import {images} from '../../assets/icons';
import {SkipassPng} from '../../assets/images';
import {getSkipassClientInfo, registerSkipassClient, unregisterSkipassClient} from '../../redux/actions/skipass';
import {skipassClientInfoSelector} from '../../redux/selectors/skipass';
import {Breadcrumbs} from '../../shared/components/breadcrumbs';
import {Button} from '../../shared/components/button';
import {ContentWithRightImage} from '../../shared/components/contentWithRightImage';
import {Image} from '../../shared/components/image';
import {LoadableContent} from '../../shared/components/loadableContent';
import {ResponsiveView} from '../../shared/components/responsiveView';
import {CustomScrollView} from '../../shared/components/scrollView';
import {Text} from '../../shared/components/text';
import {useStyles} from '../../shared/hooks/styles';
import {useIsDesktop} from '../../shared/hooks/useIsDesktop';
import {Routes, Screens} from '../../shared/routing';
import Screen from '../../shared/components/screen';
import {DeviceSize} from '../../shared/theme/helpers';
import {NotRegisteredSkipass} from './notRegisteredSkipass';
import {RegisteredSkipass} from './registeredSkipass';
import {holderProfileSelector} from '../../redux/selectors/profile';
import {useAppDispatch} from '../../redux';

export function Skipass() {
  const dispatch = useAppDispatch();
  const routes = [
    {
      title: 'Возможности карты',
      routeName: Screens.CardServices,
      webRoute: Routes.cardService,
    },
    {
      title: 'Ски-пасс',
      routeName: Screens.Skipass,
      webRoute: Routes.skipass,
    },
  ];

  const styles = useStyles(({theme, mediaOnly, deviceSize}) => ({
    screen: {
      backgroundColor: theme.screen.container[deviceSize].backgroundColor,
    },
    sectionTitle: {
      ...theme.fonts.h2,
      marginBottom: 30,
      marginTop: 40,
    },
    sectionContainer: {
      overflow: 'hidden',
      marginTop: 32,
      ...mediaOnly(DeviceSize.desktop, {paddingHorizontal: 24}, {paddingHorizontal: 16}),
    },
    container: {
      ...mediaOnly(DeviceSize.desktop, {marginBottom: theme.screen.bottomOffset}),
    },
    sectionWithImageContainer: {
      paddingBottom: 40,
    },
    sectionWithImageContent: mediaOnly(DeviceSize.desktop, {
      minHeight: 0,
      maxWidth: '80%',
      width: '100%',
    }),
    sectionWithImageBg: mediaOnly(DeviceSize.desktop, {
      width: 444,
      height: 457,
      top: -93,
    }),
    sectionWithImageIcon: {
      top: -93,
      left: -180,
    },
    sectionWithContent: {
      maxWidth: 820,
    },
    buttonText: {
      color: 'red',
    },
    blockButton: {
      paddingHorizontal: 10,
      paddingVertical: 8,
      height: 'auto',
      margin: 0,
      ...mediaOnly(
        DeviceSize.mobile,
        {
          marginTop: 20,
          width: 260,
        },
        {
          marginLeft: 20,
          width: 240,
        },
      ),
    },
  }));
  const {clientInfo, isLoading} = useSelector(skipassClientInfoSelector());
  const holderProfile = useSelector(holderProfileSelector());
  const isDesktop = useIsDesktop();
  const isRegistered = useMemo(() => {
    return clientInfo != null && !clientInfo.clientAccountInfo.isBlocked && holderProfile?.skiPassId;
  }, [clientInfo, holderProfile]);

  const handleRegisterPress = useCallback(() => {
    dispatch(registerSkipassClient());
  }, []);

  const handleBlockPress = useCallback(() => {
    dispatch(unregisterSkipassClient());
  }, []);

  useLayoutEffect(() => {
    dispatch(getSkipassClientInfo());
  }, []);

  const Content = isRegistered ? (
    <RegisteredSkipass onBlockPress={handleBlockPress} />
  ) : (
    <NotRegisteredSkipass onRegisterPress={handleRegisterPress} />
  );

  return (
    <Screen>
      <CustomScrollView
        contentInsetAdjustmentBehavior='automatic'
        onRefresh={() => {
          dispatch(getSkipassClientInfo());
        }}>
        <View style={styles.sectionContainer}>
          <Breadcrumbs style={{flex: undefined}} routes={routes} />
          <ResponsiveView only={[DeviceSize.desktop]}>
            <Text testID='transport-app-id' style={styles.sectionTitle}>
              Ски-пасс
            </Text>
          </ResponsiveView>
          <View style={styles.container}>
            <ContentWithRightImage
              containerStyle={styles.sectionWithImageContainer}
              contentStyle={styles.sectionWithImageContent}
              backgroundStyle={styles.sectionWithImageBg}
              iconStyle={styles.sectionWithImageIcon}
              hideImage={!isDesktop}
              icon={
                <View style={{alignItems: 'center'}}>
                  <Image source={SkipassPng as any} width={483} height={430} />
                  {isRegistered && (
                    <Button
                      id='skipass-block-btn'
                      iconPosition='left'
                      icon={images.SignOut}
                      type='secondary'
                      text='Отвязать кошелек'
                      onPress={handleBlockPress}
                      textStyle={styles.buttonText}
                      style={styles.blockButton}
                    />
                  )}
                </View>
              }>
              <LoadableContent loading={isLoading}>{Content}</LoadableContent>
            </ContentWithRightImage>
          </View>
        </View>
      </CustomScrollView>
    </Screen>
  );
}
